import "../../styles/quotes.scss";
import quotesData from "../../data/quotes.json";
import React, { useState, useMemo } from "react";

const QuotesPage = () => {
  const [quotes] = useState(quotesData);
  const [sortConfig, setSortConfig] = useState({
    key: "attribution",
    direction: "ascending",
  });
  const [searchFilter, setSearchFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");

  // Extract unique tags for filter dropdown
  const allTags = useMemo(() => {
    const set = new Set();
    quotes.forEach((q) => {
      if (q.tags) q.tags.forEach((t) => set.add(t));
    });
    return [...set].sort();
  }, [quotes]);

  // Filter quotes
  const filteredQuotes = useMemo(() => {
    return quotes.filter((q) => {
      // Tag filter
      if (tagFilter && !q.tags?.includes(tagFilter)) {
        return false;
      }

      // Search filter (searches quote, attribution, and tags)
      if (searchFilter) {
        const searchLower = searchFilter.toLowerCase();
        const quoteMatch = q.quote?.toLowerCase().includes(searchLower);
        const attrMatch = q.attribution?.toLowerCase().includes(searchLower);
        const tagMatch = q.tags?.some((t) =>
          t.toLowerCase().includes(searchLower)
        );
        if (!quoteMatch && !attrMatch && !tagMatch) return false;
      }

      return true;
    });
  }, [quotes, searchFilter, tagFilter]);

  // Sort filtered quotes
  const sortedQuotes = useMemo(() => {
    let sortable = [...filteredQuotes];
    sortable.sort((a, b) => {
      const va = a[sortConfig.key] ? a[sortConfig.key].toLowerCase() : "";
      const vb = b[sortConfig.key] ? b[sortConfig.key].toLowerCase() : "";
      if (va < vb) return sortConfig.direction === "ascending" ? -1 : 1;
      if (va > vb) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });
    return sortable;
  }, [filteredQuotes, sortConfig]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const SortButton = ({ label, sortKey }) => (
    <button onClick={() => requestSort(sortKey)} className="sort-button">
      {label}
      <span aria-hidden="true">
        {sortConfig.key === sortKey
          ? sortConfig.direction === "ascending"
            ? " ▲"
            : " ▼"
          : ""}
      </span>
    </button>
  );

  return (
    <div id="root" className="quotes-page">
      <h1>Quotes & Lyrics</h1>

      <div className="quotes-stats">
        <span className="total-count">
          <strong>{quotes.length}</strong> quotes saved
        </span>
      </div>

      <div className="quotes-controls">
        <div className="filters-row">
          <input
            type="text"
            placeholder="Search quotes, attributions, tags..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="search-input"
          />
          <select
            value={tagFilter}
            onChange={(e) => setTagFilter(e.target.value)}
            className="tag-filter"
          >
            <option value="">All Tags</option>
            {allTags.map((tag) => (
              <option key={tag} value={tag}>
                {tag}
              </option>
            ))}
          </select>
          {(searchFilter || tagFilter) && (
            <button
              onClick={() => {
                setSearchFilter("");
                setTagFilter("");
              }}
              className="clear-filters"
            >
              Clear
            </button>
          )}
        </div>
        <div className="sort-controls">
          <span className="control-label">Sort by:</span>
          <SortButton label="Quote" sortKey="quote" />
          <SortButton label="Attribution" sortKey="attribution" />
        </div>
        {(searchFilter || tagFilter) && (
          <div className="filter-status">
            Showing {sortedQuotes.length} of {quotes.length} quotes
          </div>
        )}
      </div>

      <div className="quotes-list">
        {sortedQuotes.map((item, index) => (
          <blockquote key={index} className="quote-card">
            <p className="quote-text">{item.quote}</p>
            <footer className="quote-footer">
              <span className="quote-attribution">
                {item.speaker && <span className="quote-speaker">{item.speaker}, </span>}
                — {item.attribution}
              </span>
              {item.tags && item.tags.length > 0 && (
                <span className="quote-tags">
                  {item.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="tag"
                      onClick={() => setTagFilter(tag)}
                    >
                      {tag}
                    </span>
                  ))}
                </span>
              )}
            </footer>
          </blockquote>
        ))}
      </div>
    </div>
  );
};

export default QuotesPage;
